import { Inject, Injectable, Optional } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams, HttpErrorResponse } from '@angular/common/http';
import { Observable, catchError, throwError, firstValueFrom, EmptyError, repeat, map, mergeMap, forkJoin, of, switchMap, first, delay, take } from 'rxjs';
import { tap } from 'rxjs/operators';
import * as xml2js from 'xml2js';

import * as dao from './shared/dao';
export { dao };
import * as utils from './shared/utils';
import * as constants from './shared/constants';
import { StatusCode, statusCodes, getStatusCodeByValue } from './shared/areaStatusCodes';
import { ApiclientService } from './shared/apiclient.service';
import { ValuesService } from './shared/values.service';

const headers = { 'content-type': 'application/json' };

// Access to mercalink FSE related tables
@Injectable({
  providedIn: 'root',
})
export class MercalinkAPIService {
  constructor(private http: HttpClient) {}

  public getPatientByNIR(server_url: string, nir: string) {
    return this.http.get<void>(server_url + '/hc/patient/' + nir);
  }

  public getRecipient$(server_url: string, assure: string, datbirth: string, orderbirth: number): Observable<dao.Patient[]> {
    console.log('[getRecipient$] datbirth: ', datbirth);
    return this.http.get<dao.Patient[]>(`${server_url}/hc/recipient?assure=${assure}&datbirth=${datbirth}&orderbirth=${orderbirth}`).pipe(take(1));
  }

  public getPatientPrescribers$(server_url: string, patient_id: string): Observable<dao.ApiResponse<string[]>> {
    return this.http.get<dao.ApiResponse<string[]>>(`${server_url}/hc/patient/${encodeURIComponent(patient_id)}/prescribers`).pipe(take(1));
  }

  // Get FSE_DOC (w/ content) from doc id. Always returns a vec, potentially empty
  public getDocById$(server_url: string, id: number): Observable<dao.FseDoc[]> {
    return this.http.get<dao.FseDoc[]>(`${server_url}/hc/attach/${id}`);
  }

  public getDocsByFseId$(server_url: string, id_fse: number): Observable<any> {
    return this.http.get<void>(`${server_url}/hc/attachs/${id_fse}`);
  }

  public postAttach$(server_url: string, formData: FormData): Observable<any> {
    return this.http.post<void>(`${server_url}/hc/attach`, formData);
  }

  public postDocEvent$(server_url: string, data: any): Observable<any> {
    let event: any = {
      id: 0,
      typ: 2, // 1: Insert, 2: Update
      userid: 0,
      objectid: 0,
      objecttyp: 16, // OBJECTTYP_HCDOC
      data: data,
    };
    return this.http.post<any>(`${server_url}/event`, event, {
      headers: headers,
    });
  }

  public postDocUpdateIdPJEvent$(server_url: string, data: any): Observable<any> {
    let event: any = {
      id: 0,
      typ: constants.EVENTTYP_FSE_UPDATEIDPJ,
      userid: 0,
      objectid: 0,
      objecttyp: constants.OBJECTTYP_HCDOC, // OBJECTTYP_HCDOC
      data: data,
    };
    return this.http.post<any>(`${server_url}/event`, event, {
      headers: headers,
    });
  }

  public postStationUpdateModeAdri$(server_url: string, data: any): Observable<any> {
    let event: any = {
      id: 0,
      typ: constants.EVENTTYP_FSE_UPDATEMODEADRI,
      userid: 0,
      objectid: 0,
      objecttyp: constants.OBJECTTYP_STATION,
      data: data,
    };
    return this.http.post<any>(`${server_url}/event`, event, {
      headers: headers,
    });
  }

  public postUserDataUpdate$(server_url: string, objectid: number, data: any): Observable<any> {
    // Update c_user.data w/ selected PS on PS card
    let event: any = {
      id: 0,
      typ: constants.EVENTTYP_FSE_UPDATEUSERDATA,
      userid: 0,
      objectid: objectid,
      objecttyp: constants.OBJECTTYP_USER,
      data: data,
    };
    return this.http.post<any>(`${server_url}/event`, event, {
      headers: headers,
    });
  }

  public postDocDeleteEvent$(server_url: string, data: any): Observable<any> {
    let event: any = {
      id: 0,
      typ: constants.EVENTTYP_DELETE,
      userid: 0,
      objectid: 0,
      objecttyp: constants.OBJECTTYP_HCDOC, // OBJECTTYP_HCDOC
      data: data,
    };
    return this.http.post<any>(`${server_url}/event`, event, {
      headers: headers,
    });
  }
}

// High level FSE functions via AreFSE API

@Injectable({
  providedIn: 'root',
})
export class MercalinkFselibService {
  // private readonly NOM_RESS_LECT = 'PCSC'; // Extracted hardcoded value as a constant
  xmlparser: xml2js.Parser = new xml2js.Parser({
    strict: true,
    trim: false,
    explicitArray: false,
  });

  constructor(
    private areaapi: AreaApiService,
    private apiclientService: ApiclientService,
    private valuesService: ValuesService,
  ) {}

  public lireCPS(area_baseurl: string, numIdentFact: string): Observable<dao.LireCPSHTTPResponse | undefined> {
    let areaCPS: dao.LireCPSHTTPResponse | undefined;

    let url = area_baseurl + 'lireCPS?nomRessLect=PCSC&codePorteurPS=&slotCPS=';
    return this.areaapi.getData(area_baseurl, url, numIdentFact).pipe(
      switchMap(xmldata => {
        return new Observable<dao.LireCPSHTTPResponse>(observer => {
          this.xmlparser.parseString(xmldata ?? '', (err: any, lireCPSResponse: dao.LireCPSResponse) => {
            if (err) {
              console.error('[getData] JSON parsing error: ', err);
              observer.next(err); // TODO Deal w/error
            } else {
              //console.log("jsono: ", JSON.stringify(jsono));
              areaCPS = lireCPSResponse.HTTPResponse;
              observer.next(areaCPS);
              observer.complete();
            }
          });
        });
      }),
      catchError(error => {
        console.error('[lireCV] Error fetching data: ', error);
        return of(undefined); // Use of(undefined) to propagate errors
      }),
    );
  }

  public lireCV(area_baseurl: string, numIdentFact: string, dateConsultation?: string): Observable<dao.LireCVHTTPResponse | undefined> {
    console.log('[MercalinkFselibService] lireCV, area_baseurl: ', area_baseurl);
    let lireCVHTTPResponse: dao.LireCVHTTPResponse | undefined;
    let url = area_baseurl + 'lireCV?nomRessLect=PCSC';
    if (dateConsultation) url = url + `&dateConsultation=${dateConsultation}`;
    return this.areaapi.getData(area_baseurl, url, numIdentFact).pipe(
      switchMap(xmldata => {
        return new Observable<dao.LireCVHTTPResponse>(observer => {
          this.xmlparser.parseString(xmldata ?? '', (err: any, lireCVResponse: dao.LireCVResponse) => {
            if (err) {
              console.error('[postData] JSON parsing error: ', err);
              observer.next(err); // TODO Deal w/error
            } else {
              //console.log("jsono: ", JSON.stringify(jsono));
              lireCVHTTPResponse = lireCVResponse.HTTPResponse;
              if (lireCVHTTPResponse?.codeRetour == 0 || lireCVHTTPResponse?.codeRetour == 1) {
                // Process beneficiaires only if all goes well OR PS card not inserted
                if (!Array.isArray(lireCVHTTPResponse.empreinteCV.beneficiaire)) {
                  // Force beneficiaire to Array
                  const hr: any = lireCVResponse.HTTPResponse;
                  const b = hr.empreinteCV['beneficiaire'] as dao.Beneficiaire;
                  lireCVHTTPResponse.empreinteCV.beneficiaire = [];
                  lireCVHTTPResponse.empreinteCV.beneficiaire.push(b);
                }

                for (const benef of lireCVHTTPResponse.empreinteCV.beneficiaire) {
                  if (benef?.periodeCodeCouverture && !Array.isArray(benef.periodeCodeCouverture)) {
                    // Force periodeCodeCouverture to array
                    const pcc = benef.periodeCodeCouverture as dao.PeriodeCodeCouverture;
                    benef.periodeCodeCouverture = [];
                    benef.periodeCodeCouverture.push(pcc);
                  }
                  if (benef.periodeDroitsAMO && !Array.isArray(benef.periodeDroitsAMO)) {
                    // Force periodeDroitsAMO to array
                    const pda = benef.periodeDroitsAMO as dao.PeriodeDroitsAMO;
                    benef.periodeDroitsAMO = [];
                    benef.periodeDroitsAMO.push(pda);
                  }
                  if (benef.donneesMutuelle?.periodeMutuelle && !Array.isArray(benef.donneesMutuelle?.periodeMutuelle)) {
                    // Force periodeMutuelle to array
                    const pm = benef.donneesMutuelle?.periodeMutuelle as dao.PeriodeMutuelle;
                    benef.donneesMutuelle.periodeMutuelle = [];
                    benef.donneesMutuelle.periodeMutuelle.push(pm);
                  }
                }
              }
              observer.next(lireCVHTTPResponse);
              observer.complete();
            }
          });
        });
      }),
      catchError(error => {
        console.error('[lireCV] Error fetching data: ', error);
        return of(undefined); // Use of(undefined) to propagate errors
      }),
    );
  }

  public extraireFacturesJamaisMisesEnLot(area_baseurl: string, numIdentFact: string): Observable<dao.ExtractFactures> {
    console.log('[MercalinkFselibService] extraireFacturesJamaisMisesEnLot, area_baseurl: ', area_baseurl);
    let params = new HttpParams().set('numIdentFact', numIdentFact);
    return this.areaapi.postData(area_baseurl, 'extraireFacturesJamaisMisesEnLot', params, numIdentFact).pipe(
      map((areaResponse: dao.ExtractFacturesHTTPResponse) => {
        if (areaResponse.HTTPResponse.codeRetour != '0') {
          console.error(`code retour: ${getStatusCodeByValue(+areaResponse.HTTPResponse.codeRetour)?.nom}`);
        }
        return areaResponse.HTTPResponse.extractFactures ?? [];
      }),
    );
  }

  public recupererListeLibellesTauxExonerations(area_baseurl: string, regime: string, numIdentFact: string): Observable<dao.ListeLibellesTaux> {
    console.log('[MercalinkFselibService] recupererListeLibellesTauxExonerations, area_baseurl: ', area_baseurl);
    const params = new HttpParams().set('regime', regime);
    return this.areaapi.postData(area_baseurl, 'recupererListeLibellesTauxExonerations', params, numIdentFact).pipe(
      map((areaResponse: dao.ListeLibellesTauxHTTPResponse) => {
        const codeRetour = areaResponse.HTTPResponse.codeRetour;
        if (codeRetour != '0') {
          const msg = getStatusCodeByValue(+codeRetour)?.nom ?? `Erreur code ${codeRetour}`;
          console.error(`[recupererListeLibellesTauxExonerations] ERROR: ${msg}`);
          const err: any = new Error(msg);
          err.codeRetour = codeRetour;
          throw err; // provoque le channel error de l'Observable
        }
        return areaResponse.HTTPResponse.listeLibellesTaux ?? [];
      }),
    );
  }

  public recupererListeNirFictifs$(area_baseurl: string, numIdentFact: string): Observable<dao.ListeNirFictifs> {
    console.log('[MercalinkFselibService] recupererListeNirFictifs, area_baseurl: ', area_baseurl);
    const params = new HttpParams();
    return this.areaapi.postData(area_baseurl, 'recupererListeNirFictifs', params, numIdentFact).pipe(
      map((areaResponse: dao.RecupererListeNirFictifsHTTPResponse) => {
        const codeRetour = areaResponse.HTTPResponse.codeRetour;
        if (codeRetour != '0') {
          const msg = getStatusCodeByValue(+codeRetour)?.nom ?? `Erreur code ${codeRetour}`;
          console.error(`[recupererListeNirFictifs] ERROR: ${msg}`);
          const err: any = new Error(msg);
          err.codeRetour = codeRetour;
          throw err; // provoque le channel error de l'Observable
        }
        return areaResponse.HTTPResponse.listeNirFictifs ?? [];
      }),
    );
  }

  public extraireLots$(area_baseurl: string, numIdentFact: string, dateHeureDerniereGeneration: string): Observable<dao.ExtractLots> {
    // Calls AF extraireLots
    console.log('[MercalinkFselibService] extraireLots, area_baseurl: ', area_baseurl);

    // Construct HTTP parameters
    const params = new HttpParams().set('numIdentFact', numIdentFact).set('dateHeureDerniereGeneration', dateHeureDerniereGeneration);

    // Make the API call
    return this.areaapi.postData(area_baseurl, 'extraireLots', params, numIdentFact).pipe(
      map((areaResponse: dao.extractLotsHTTPResponse) => {
        // Check response code and log if necessary
        if (areaResponse.HTTPResponse.codeRetour !== '0') {
          console.error(`[MercalinkFselibService] extraireLots failed with codeRetour: ${areaResponse.HTTPResponse.codeRetour} - ${getStatusCodeByValue(+areaResponse.HTTPResponse.codeRetour)?.nom}`);
        }
        if (!areaResponse.HTTPResponse.extractLots?.lot) {
          // No <lot> => return an empty array
          areaResponse.HTTPResponse.extractLots.lot = [];
        }
        if (!Array.isArray(areaResponse.HTTPResponse.extractLots.lot)) {
          // Force lot to Array
          const lot: any = areaResponse.HTTPResponse.extractLots.lot;
          areaResponse.HTTPResponse.extractLots.lot = [];
          areaResponse.HTTPResponse.extractLots.lot.push(lot);
        }
        return areaResponse.HTTPResponse.extractLots; // Return the extracted lots
      }),
      catchError(error => {
        // Handle errors from the API call
        console.error('[MercalinkFselibService] extraireLots encountered an error:', error);
        return throwError(() => new Error('Failed to retrieve lots'));
      }),
    );
  }

  public extraireRSPs$(area_baseurl: string, numIdentFact: string, dateHeureDerniereGeneration: string): Observable<dao.ExtractRSPs> {
    console.log('[MercalinkFselibService] extraireRSPs, area_baseurl: ', area_baseurl);

    // Construct HTTP parameters
    const params = new HttpParams().set('numIdentFact', numIdentFact).set('dateHeureDerniereGeneration', dateHeureDerniereGeneration).set('topAvecIdFic', '1'); // Force get idFic

    // Make the API call
    return this.areaapi.postData(area_baseurl, 'extraireRSPs', params, numIdentFact).pipe(
      map((areaResponse: dao.extractRSPsHTTPResponse) => {
        // Check response code and log if necessary
        if (areaResponse.HTTPResponse.codeRetour !== '0') {
          console.error(`[MercalinkFselibService] extraireRSPs failed with codeRetour: ${areaResponse.HTTPResponse.codeRetour} - ${getStatusCodeByValue(+areaResponse.HTTPResponse.codeRetour)?.nom}`);
        }
        if (!areaResponse.HTTPResponse.extractRSPs?.fichierRSP) {
          // No <lot> => return an empty array
          areaResponse.HTTPResponse.extractRSPs.fichierRSP = [];
        }
        if (!Array.isArray(areaResponse.HTTPResponse.extractRSPs.fichierRSP)) {
          // Force rsps to Array
          const rsps: any = areaResponse.HTTPResponse.extractRSPs.fichierRSP;
          areaResponse.HTTPResponse.extractRSPs.fichierRSP = [];
          areaResponse.HTTPResponse.extractRSPs.fichierRSP.push(rsps);
        }
        return areaResponse.HTTPResponse.extractRSPs; // Return the extracted rsps
      }),
      catchError(error => {
        // Handle errors from the API call
        console.error('[MercalinkFselibService] extraireRSPs encountered an error:', error);
        return throwError(() => new Error('Failed to retrieve rsps'));
      }),
    );
  }

  public extraireSCOR$(area_baseurl: string, numIdentFact: string, dateHeureDerniereGeneration: string): Observable<dao.ExtractSCOR> {
    //console.log('[MercalinkFselibService] extraireSCOR, area_baseurl: ', area_baseurl);

    // Construct HTTP parameters
    const params = new HttpParams().set('numIdentFact', numIdentFact).set('dateHeureDerniereGeneration', dateHeureDerniereGeneration);

    // Make the API call
    return this.areaapi.postData(area_baseurl, 'extraireSCOR', params, numIdentFact).pipe(
      map((areaResponse: dao.ExtractScoreHTTPResponse) => {
        // Check response code and log if necessary
        if (areaResponse.HTTPResponse.codeRetour !== '0') {
          console.error(`[MercalinkFselibService] extraireLots failed with codeRetour: ${areaResponse.HTTPResponse.codeRetour} - ${getStatusCodeByValue(+areaResponse.HTTPResponse.codeRetour)?.nom}`);
        }
        if (!areaResponse.HTTPResponse.extractSCOR?.dossier) {
          // No <dossier> => return an empty array
          areaResponse.HTTPResponse.extractSCOR.dossier = [];
        }
        if (!Array.isArray(areaResponse.HTTPResponse.extractSCOR.dossier)) {
          // Force dossier to Array
          const dossier: any = areaResponse.HTTPResponse.extractSCOR.dossier;
          areaResponse.HTTPResponse.extractSCOR.dossier = [];
          areaResponse.HTTPResponse.extractSCOR.dossier.push(dossier);
        }
        return areaResponse.HTTPResponse.extractSCOR; // Return the extracted dossiers
      }),
      catchError(error => {
        // Handle errors from the API call
        console.error('[MercalinkFselibService] extraireSCOR encountered an error:', error);
        return throwError(() => new Error('Failed to retrieve dossiers SCOR'));
      }),
    );
  }

  // Cette fonction permet de regrouper les FSE et les DRE en lots
  // Point de départ de la chaîne de traitement : lot / fichier / transmission
  public mettreEnLot$(area_baseurl: string, numIdentFact: string, numSituation: string, configarea: dao.ConfigArea): Observable<dao.AreaResponseType> {
    //console.log('[MercalinkFselibService] mettreEnLot, area_baseurl: ', area_baseurl);

    // Construct HTTP parameters
    const params = new HttpParams()
      .set('numIdentFact', numIdentFact)
      .set('nomRessLect', configarea.nomRessLect)
      .set('numAgrement', configarea.numAutorisationSocle)
      .set('numSituation', numSituation);

    // Make the API call
    return this.areaapi.postData(area_baseurl, 'mettreEnLot', params, numIdentFact).pipe(
      map((areaResponse: dao.AreaResponseType) => {
        // Check response code and log if necessary
        if (areaResponse.HTTPResponse.codeRetour !== '0') {
          console.error(`[MercalinkFselibService] mettreEnLot failed with codeRetour: ${areaResponse.HTTPResponse.codeRetour} - ${getStatusCodeByValue(+areaResponse.HTTPResponse.codeRetour)?.nom}`);
        }
        return areaResponse; // Return the response regardless of the error
      }),
      catchError(error => {
        // Handle errors from the API call
        console.error('[MercalinkFselibService] mettreEnLot encountered an error:', error);
        return throwError(() => new Error('[mettreEnLot] Erreur'));
      }),
    );
  }

  // Cette fonction permet de regrouper les lots en fichiers en fonction des critères de regroupement de la norme B2.
  // Appelée normalement après mettreEnLot
  public mettreEnFichier$(area_baseurl: string, numIdentFact: string, numAgrement: string, numSituation: string, zoneMsg: string): Observable<dao.AreaResponseType> {
    //console.log('[MercalinkFselibService] mettreEnFichier, area_baseurl: ', area_baseurl);

    // Construct HTTP parameters
    const params = new HttpParams().set('numIdentFact', numIdentFact).set('zoneMsg', zoneMsg);

    // Make the API call
    return this.areaapi.postData(area_baseurl, 'mettreEnFichier', params, numIdentFact).pipe(
      map((areaResponse: dao.AreaResponseType) => {
        // Check response code and log if necessary
        if (areaResponse.HTTPResponse.codeRetour !== '0') {
          console.error(`[MercalinkFselibService] mettreEnFichier failed with codeRetour: ${areaResponse.HTTPResponse.codeRetour} - ${getStatusCodeByValue(+areaResponse.HTTPResponse.codeRetour)?.nom}`);
        }
        return areaResponse; // Return the response regardless of the error
      }),
      catchError(error => {
        // Handle errors from the API call
        console.error('[MercalinkFselibService] mettreEnFichier encountered an error:', error);
        return throwError(() => new Error('[mettreEnFichier] Erreur'));
      }),
    );
  }

  // La fonction <teletransmettre> effectue séquentiellement le vidage de la BAL du PS puis l'envoi des fichiers prêts à envoyer
  // Appelée normalement après mettreEnFichier
  public teletransmettre$(area_baseurl: string, numIdentFact: string): Observable<dao.AreaResponseType> {
    //console.log('[MercalinkFselibService] teletransmettre, area_baseurl: ', area_baseurl);

    // Construct HTTP parameters
    const params = new HttpParams().set('numIdentFact', numIdentFact).set('topEnvoyer', '1');
    //.set('nomRessLect', this.NOM_RESS_LECT)
    //.set('numAgrement', numAgrement)
    //.set('numSituation', numSituation);

    // Make the API call
    return this.areaapi.postData(area_baseurl, 'teletransmettre', params, numIdentFact).pipe(
      map((areaResponse: dao.AreaResponseType) => {
        // Check response code and log if necessary
        if (areaResponse.HTTPResponse.codeRetour !== '0') {
          console.error(`[MercalinkFselibService] teletransmettre failed with codeRetour: ${areaResponse.HTTPResponse.codeRetour} - ${getStatusCodeByValue(+areaResponse.HTTPResponse.codeRetour)?.nom}`);
        }
        return areaResponse; // Return the response regardless of the error
      }),
      catchError(error => {
        // Handle errors from the API call
        console.error('[MercalinkFselibService] teletransmettre encountered an error:', error);
        return throwError(() => new Error('[MercalinkFselibService] teletransmettre Erreur'));
      }),
    );
  }

  // Send PJ to area by calling ajouterPJ Area endpoint
  public ajouterPJ$(area_baseurl: string, numIdentFact: string, numFacture: string, dateFacture: string, pj: any): Observable<dao.AjouterPJHTTPResponse> {
    //console.log('[MercalinkFselibService] ajouterPJ, area_baseurl: ', area_baseurl);

    const builder = new xml2js.Builder({
      xmldec: { version: '1.0', encoding: 'UTF-8' },
      renderOpts: {
        pretty: true,
        indent: '  ',
        newline: '\n',
      },
      headless: false,
      rootName: 'PJ',
    });

    let xml = builder.buildObject(pj);
    xml = xml.substring(xml.indexOf('\n') + 1, xml.length); // Remove fist line (<?xml version ...)
    // Construct HTTP parameters
    const params = new HttpParams().set('numIdentFact', numIdentFact).set('numFacture', numFacture).set('dateFacture', dateFacture).set('pj', xml);

    //console.log('[ajouterpj$] xml: ', xml);
    //console.log('[ajouterpj$]');

    // Make the API call
    return this.areaapi.postData(area_baseurl, 'ajouterPJ', params, numIdentFact).pipe(
      // Call Area ajouterPJ endpoint
      map((areaResponse: dao.AjouterPJHTTPResponse) => {
        // Check response code and log if necessary
        if (areaResponse.HTTPResponse.codeRetour !== '0') {
          console.error(`[MercalinkFselibService] ajouterPJ failed with codeRetour: ${areaResponse.HTTPResponse.codeRetour} - ${getStatusCodeByValue(+areaResponse.HTTPResponse.codeRetour)?.nom}`);
        }
        return areaResponse; // Return the response regardless of the error
      }),
      catchError(error => {
        // Handle errors from the API call
        console.error('[MercalinkFselibService] ajouterPJ encountered an error:', error);
        return throwError(() => new Error('[ajouterPJ] Erreur'));
      }),
    );
  }

  public supprimerPJ$(area_baseurl: string, numIdentFact: string, numFacture: string, dateFacture: string, idPJ: string): Observable<dao.SupprimerHTTPResponse> {
    //console.log('[MercalinkFselibService] supprimerPJ$, area_baseurl: ', area_baseurl);

    const params = new HttpParams().set('numIdentFact', numIdentFact).set('numFacture', numFacture).set('dateFacture', dateFacture).set('idPJ', idPJ);

    // Make the API call
    return this.areaapi.postData(area_baseurl, 'supprimerPJ', params, numIdentFact).pipe(
      // Call Area supprimerPJ endpoint
      map((areaResponse: dao.SupprimerHTTPResponse) => {
        // Check response code and log if necessary
        if (areaResponse.HTTPResponse.codeRetour !== '0') {
          console.error(`[MercalinkFselibService] supprimerPJ failed with codeRetour: ${areaResponse.HTTPResponse.codeRetour} - ${getStatusCodeByValue(+areaResponse.HTTPResponse.codeRetour)?.nom}`);
        }
        return areaResponse; // Return the response regardless of the error
      }),
      catchError(error => {
        // Handle errors from the API call
        console.error('[MercalinkFselibService] supprimerPJ encountered an error:', error);
        return throwError(() => new Error('[supprimerPJ] Erreur'));
      }),
    );
  }

  // Remove a FSE (non mise en lot => 'SECURISEE')
  public supprimerFSE$(area_baseurl: string, numIdentFact: string, numFacture: string, dateFacture: string): Observable<dao.SupprimerHTTPResponse> {
    //console.log('[MercalinkFselibService] supprimerFSE$, area_baseurl: ', area_baseurl);

    if (!numIdentFact) {
      return throwError(() => new Error('[supprimerFSE$] numIdentFact is mandatory'));
    }

    const params = new HttpParams().set('numIdentFact', numIdentFact).set('numFacture', numFacture).set('dateFacture', dateFacture);

    // Make the API call
    return this.areaapi.postData(area_baseurl, 'supprimerFacture', params, numIdentFact).pipe(
      // Call Area supprimerFacture endpoint
      map((areaResponse: dao.SupprimerHTTPResponse) => {
        // WARNING reused this interface as it's the same one
        // Check response code and log if necessary
        if (areaResponse.HTTPResponse.codeRetour !== '0') {
          console.error(`[MercalinkFselibService] supprimerFSE failed with codeRetour: ${areaResponse.HTTPResponse.codeRetour} - ${getStatusCodeByValue(+areaResponse.HTTPResponse.codeRetour)?.nom}`);
        }
        return areaResponse; // Return the response regardless of the error
      }),
      catchError(error => {
        // Handle errors from the API call
        console.error('[MercalinkFselibService] supprimerFSE encountered an error:', error);
        return throwError(() => new Error('[supprimerFSE] Erreur'));
      }),
    );
  }

  public recupererParamPS$(area_baseurl: string, numIdentFact: string): Observable<dao.RecupererParamPS | undefined> {
    const params = new HttpParams().set('numIdentFact', numIdentFact);

    // Make the API call
    return this.areaapi.postData(area_baseurl, 'recupererParamPS', params, numIdentFact).pipe(
      // Call Area supprimerFacture endpoint
      map((areaResponse: dao.RecupererParamPS) => {
        // WARNING reused this interface as it's the same one
        // Check response code and log if necessary
        if (areaResponse.HTTPResponse.codeRetour !== '0') {
          console.error(`[MercalinkFselibService] recupererParamPS failed with codeRetour: ${areaResponse.HTTPResponse.codeRetour} - ${getStatusCodeByValue(+areaResponse.HTTPResponse.codeRetour)?.nom}`);
        }
        return areaResponse; // Return the response regardless of the error
      }),
      catchError(error => {
        // Handle errors from the API call
        console.error('[MercalinkFselibService] recupererParamPS encountered an error:', error);
        return throwError(() => new Error('[recupererParamPS] Erreur'));
      }),
    );
  }

  /**
   * Builds the XML string for ParamPS based on the provided object.
   * @param paramPS The ParamPS object.
   * @returns The XML string.
   */
  private buildParamPSXml(paramPS: dao.ParamPS): string {
    const builder = new xml2js.Builder({
      xmldec: { version: '1.0', encoding: 'UTF-8' },
      renderOpts: {
        pretty: true,
        indent: '  ',
        newline: '\n',
      },
      headless: false,
      rootName: 'paramPS', // Root element name
    });

    // Create an object that matches the desired XML structure
    const xmlObject: any = {
      numIdentFact: paramPS.numIdentFact,
      codeSpec: paramPS.codeSpec,
      typeEmet: paramPS.typeEmet,
      numEmet: paramPS.numEmet,
      mailFact: paramPS.mailFact,
      urlSMTP: paramPS.urlSMTP,
      portSMTP: paramPS.portSMTP,
      sslSMTP: paramPS.sslSMTP, // New field
      authSMTP: paramPS.authSMTP,
      loginSMTP: paramPS.loginSMTP, // New field
      passSMTP: paramPS.passSMTP, // New field
      urlPOP3: paramPS.urlPOP3,
      portPOP3: paramPS.portPOP3,
      loginPOP3: paramPS.loginPOP3,
      passPOP3: paramPS.passPOP3,
      topCompression: paramPS.topCompression,
      topChiffrement: paramPS.topChiffrement,
      topAffichDiagSTS: paramPS.topAffichDiagSTS,
      topDesactivSTS: paramPS.topDesactivSTS,
      topSupprimerNonSV: paramPS.topSupprimerNonSV,
      nbMaxFseParLot: paramPS.nbMaxFseParLot,

      modeFonc: paramPS.modeFonc, // OCT
      numOCT: paramPS.numOCT,
      typeDestOCT: paramPS.typeDestOCT,
      appliOCT: paramPS.appliOCT,
      balOCT: paramPS.balOCT,
      topFSEEnrichieOCT: 0,

      modeSCOR: paramPS.modeSCOR,
      typeDestinaireARLSCOR: paramPS.typeDestinaireARLSCOR,
      codeRegionSCOR: paramPS.codeRegionSCOR,
      tailleMaxPDFSCOR: paramPS.tailleMaxPDFSCOR,
      tailleMaxLotSCOR: paramPS.tailleMaxLotSCOR,
    };

    utils.removeEmptyProperty(xmlObject, 'typeDestOCT');
    utils.removeEmptyProperty(xmlObject, 'appliOCT');

    let xml = builder.buildObject(xmlObject);
    // Remove the XML declaration as per the example model
    xml = xml.substring(xml.indexOf('\n') + 1, xml.length);
    return xml;
  }

  public modifierParamPS(area_baseurl: string, paramPS: dao.ParamPS, numIdentFact: string): Observable<dao.AreaResponseType | undefined> {
    const paramPSxmldata = this.buildParamPSXml(paramPS); // Build XML from object
    const url = 'modifierParamPS'; // Endpoint name
    const numId = numIdentFact || (paramPS?.numIdentFact ? String(paramPS.numIdentFact) : '');
    if (!numId) {
      return throwError(() => new Error('[modifierParamPS] numIdentFact is mandatory'));
    }

    const params = new HttpParams().set('paramPS', paramPSxmldata);
    return this.areaapi.postData(area_baseurl, url, params, numId);
  }

  public consulterADR$(area_baseurl: string, critereADRxmldata: string, topActionPS: string, dateConsultation: string, numIdentFact: string, nomLogiciel: string, versionCommerciale: string, versionADR: string, numAutorisation: string, nomRessLect: string): Observable<dao.ConsulterADRResponse> {
    const params = new HttpParams()
      .set('criteresADR', critereADRxmldata)
      .set('numIdentFact', numIdentFact)
      .set('nomRessLect', nomRessLect)
      .set('topActionPS', topActionPS) // '0' or '1'
      .set('nomLogiciel', nomLogiciel)
      .set('dateConsultation', dateConsultation)
      .set('versionCommerciale', versionCommerciale)
      .set('versionADR', versionADR)
      .set('numAutorisation', numAutorisation);

    return this.areaapi.postData(area_baseurl, 'consulterADR', params, numIdentFact).pipe(
      // Call Area supprimerFacture endpoint
      map((areaResponse: dao.ConsulterADRResponse) => {
        //console.log("[consulterADR] areaResponse: ", JSON.stringify(areaResponse));
        // Check response code and log if necessary
        if (areaResponse.HTTPResponse.codeRetour !== '0') {
          console.error(`[MercalinkFselibService] consulterADR$ failed with codeRetour: ${areaResponse.HTTPResponse.codeRetour} - ${getStatusCodeByValue(+areaResponse.HTTPResponse.codeRetour)?.nom}`);
        } else {
          const benef = areaResponse.HTTPResponse.empreinteADR.beneficiaire;

          if (!Array.isArray(benef.periodeDroitsAMO)) {
            // Force to Array
            const b = benef.periodeDroitsAMO as dao.PeriodeDroitsAMO | undefined;
            benef.periodeDroitsAMO = [];
            if (b !== undefined) {
              benef.periodeDroitsAMO.push(b);
            }
          }
          if (!Array.isArray(benef.periodeCodeCouverture)) {
            // Force to Array
            const b = benef.periodeCodeCouverture as dao.PeriodeCodeCouverture;
            benef.periodeCodeCouverture = [];
            if (b !== undefined) {
              benef.periodeCodeCouverture.push(b);
            }
          }
          if (benef.donneesMutuelle?.periodeMutuelle && !Array.isArray(benef.donneesMutuelle?.periodeMutuelle)) {
            // Force periodeMutuelle to array
            const pm = benef.donneesMutuelle?.periodeMutuelle as dao.PeriodeMutuelle;
            benef.donneesMutuelle.periodeMutuelle = [];
            benef.donneesMutuelle.periodeMutuelle.push(pm);
          }
        }
        return areaResponse; // Return the response regardless of the error
      }),
      catchError(error => {
        // Handle errors from the API call
        console.error('[MercalinkFselibService] consulterADR encountered an error:', error);
        return throwError(() => new Error('[consulterADR] Erreur'));
      }),
    );
  }

  public demanderContexteAuthentificationApCV$(area_baseurl: string, numIdentFact: string, nomRessLect: string, editeur: string, nomLogiciel: string, versionApCV: string, versionCommerciale: string, numAutorisation: string, donneesApCV: string, slotCPS?: string): Observable<dao.demanderContexteAuthentificationApCVResponse> {
    let params = new HttpParams().set('numIdentFact', numIdentFact).set('nomRessLect', nomRessLect).set('editeur', editeur).set('nomLogiciel', nomLogiciel).set('versionApCV', versionApCV).set('versionCommerciale', versionCommerciale).set('numAutorisation', numAutorisation).set('donneesApCV', donneesApCV);

    if (slotCPS) {
      params = params.set('slotCPS', slotCPS);
    }

    return this.areaapi.postData(area_baseurl, 'demanderContexteAuthentificationApCV', params, numIdentFact).pipe(
      map((areaResponse: dao.demanderContexteAuthentificationApCVResponse) => {
        if (areaResponse.HTTPResponse.codeRetour !== '0') {
          console.error(`[MercalinkFselibService] demanderContexteAuthentificationApCV$ failed with codeRetour: ${areaResponse.HTTPResponse.codeRetour} - ${getStatusCodeByValue(+areaResponse.HTTPResponse.codeRetour)?.nom}`);
        }
        return areaResponse;
      }),
      catchError(error => {
        console.error('[MercalinkFselibService] demanderContexteAuthentificationApCV encountered an error:', error);
        return throwError(() => new Error('[demanderContexteAuthentificationApCV] Erreur'));
      }),
    );
  }

  public restituerContexteAuthentificationApCV$(area_baseurl: string, numIdentFact: string, nomRessLect: string, editeur: string, nomLogiciel: string, versionApCV: string, versionCommerciale: string, numAutorisation: string, slotCPS?: string): Observable<dao.demanderContexteAuthentificationApCVResponse> {
    let params = new HttpParams().set('numIdentFact', numIdentFact).set('nomRessLect', nomRessLect).set('editeur', editeur).set('nomLogiciel', nomLogiciel).set('versionApCV', versionApCV).set('versionCommerciale', versionCommerciale).set('numAutorisation', numAutorisation);

    if (slotCPS) {
      params = params.set('slotCPS', slotCPS);
    }

    return this.areaapi.postData(area_baseurl, 'restituerContexteAuthentificationApCV', params, numIdentFact).pipe(
      map((areaResponse: dao.demanderContexteAuthentificationApCVResponse) => {
        if (areaResponse.HTTPResponse.codeRetour !== '0') {
          console.error(`[MercalinkFselibService] restituerContexteAuthentificationApCV$ failed with codeRetour: ${areaResponse.HTTPResponse.codeRetour} - ${getStatusCodeByValue(+areaResponse.HTTPResponse.codeRetour)?.nom}`);
        }
        return areaResponse;
      }),
      catchError(error => {
        console.error('[MercalinkFselibService] restituerContexteAuthentificationApCV encountered an error:', error);
        return throwError(() => new Error('[restituerContexteAuthentificationApCV] Erreur'));
      }),
    );
  }

  public detruireContexteAuthentificationApCV$(area_baseurl: string, numIdentFact: string, nomRessLect: string, slotCPS?: string): Observable<dao.demanderContexteAuthentificationApCVResponse> {
    let params = new HttpParams().set('numIdentFact', numIdentFact).set('nomRessLect', nomRessLect);

    if (slotCPS) {
      params = params.set('slotCPS', slotCPS);
    }

    return this.areaapi.postData(area_baseurl, 'detruireContexteAuthentificationApCV', params, numIdentFact).pipe(
      map((areaResponse: any) => {
        if (areaResponse.HTTPResponse.codeRetour !== '0') {
          console.error(`[MercalinkFselibService] detruireContexteAuthentificationApCV$ failed with codeRetour: ${areaResponse.HTTPResponse.codeRetour} - ${getStatusCodeByValue(+areaResponse.HTTPResponse.codeRetour)?.nom}`);
        }
        return areaResponse;
      }),
      catchError(error => {
        console.error('[MercalinkFselibService] detruireContexteAuthentificationApCV encountered an error:', error);
        return throwError(() => new Error('[detruireContexteAuthentificationApCV] Erreur'));
      }),
    );
  }

  public facturer(area_baseurl: string, server_url: string, fse_id: number, id_order: number, user_id: number, topSimulation: boolean, topAssistance: boolean = false, configarea: dao.ConfigArea): Observable<utils.ProcessingResult> {
    // id_order is PK for fse_order
    let processingResult: utils.ProcessingResult = new utils.ProcessingResult();

    const user$ = this.apiclientService.getUser$(server_url, user_id).pipe(
      catchError(err => {
        return of({ error: 'getUser$', details: err });
      }),
    );
    const fse$ = this.apiclientService.getFse$(server_url, fse_id).pipe(
      catchError(err => {
        return of({ error: 'fse$', details: err });
      }),
    ); // Get FSE from fse_order

    const paramObs$ = forkJoin({ user: user$, fse: fse$ }).pipe(
      tap(results => {
        console.log(`%c[facturer] forjoin result: ${JSON.stringify(results, null, 2)}`, 'color: magenta; font-weight: bold;');
      }),
      switchMap(results => {
        if (results.user.error) {
          console.error('getUser$ failed with error: ', results.user.details);
          processingResult.addFatal('Echec au chargement des paramètres utilisateur');
        }
        if ('error' in results.fse) {
          console.error('getFse$ failed with error: ', results.fse.details);
          processingResult.addFatal('Echec au chargement de la FSE ' + fse_id);
        }
        if (!results.user.error && !('error' in results.fse)) {
          // All went well so far
          //console.log('[MercalinkFselibService.facturer] forkJoin Results:\n', JSON.stringify(results, null, 2));
          try {
            if (!(Array.isArray(results?.user) && results?.user.length === 1)) {
              throw "Incohérence de données, paramètres 'user' absents";
            }
            const user = results.user[0];

            if (!(Array.isArray(results?.fse) && results.fse.length === 1)) {
              throw 'Incohérence de données FSE';
            }
            const fse = results.fse[0];

            if (!fse?.data?.factureFR) {
              throw 'Incohérence de données de facturation';
            }
            const factureFR: dao.FactureFR = new dao.FactureFR(fse.data.factureFR); // Init a factureFR without prestationFR for now

            if (factureFR == undefined) {
              throw 'Données FSE incohérentes en BD';
            } else {
              if (configarea?.PMSS) {
                factureFR.PMSS = configarea.PMSS;
              } else {
                throw "Incohérence de données 'paramètres FSE globaux', informations PMSS absentes";
              }
              //factureFR.dateFacture = utils.tofseformat(factureFR.dateFacture??utils.dateTofseFormat())??'';	// Force FSE,format. Default to today
              if (user?.data?.FSE?.PS) {
                factureFR.PS = new dao.PS(user.data.FSE.PS as dao.PSData); // Acquisition données PS from user.data
              } else {
                throw 'Incohérence de données, informations PS absentes';
              }

              // console.log("[facturer] before getPrestationFR - factureFR: ", JSON.stringify(factureFR, null, 2));
              return this.getPrestationFRs(server_url, id_order, factureFR, processingResult).pipe(
                switchMap((prestations: dao.PrestationFR[]) => {
                  console.log('After getPrestationFR - prestations', JSON.stringify(prestations, null, 2));
                  if (processingResult.fatal.length > 0) {
                    return of(processingResult);
                  }
                  factureFR.prestationFR = prestations;
                  const builder = new xml2js.Builder({
                    xmldec: { version: '1.0', encoding: 'UTF-8' },
                    renderOpts: {
                      pretty: true,
                      indent: '  ',
                      newline: '\n',
                    },
                    headless: false,
                    rootName: 'factureFR',
                  });
                  //console.log("1 " + factureFR.toOrderedObject());
                  let params = new HttpParams();
                  try {
                    let xml = builder.buildObject(factureFR.toOrderedObject());
                    xml = xml.substring(xml.indexOf('\n') + 1, xml.length); // Remove fist line (<?xml version ...)
                    //console.log('[facturer] xml:\n' + xml);

                    params = params.set('nomRessLect', configarea.nomRessLect);
                    params = params.set('nomLogiciel', configarea.nomLogiciel);
                    params = params.set('versionADR', configarea.versionADR);
                    params = params.set('versionCommerciale', configarea.versionCommerciale);
                    const numAutorisation =
                      factureFR.beneficiaire?.attestationAMO?.pieceJustifAMO === dao.PieceJustifAMO.ADR
                        ? configarea.numAutorisationAdri
                        : configarea.numAutorisationSocle;
                    params = params.set('numAutorisation', numAutorisation);
                    params = params.set('editeur', configarea.editeur);
                    params = params.set('versionAnnuaireAMC', configarea.versionAnnuaireAMC);
                    params = params.set('topSimulation', topSimulation);
                    params = params.set('topSilencieux', configarea.topSilencieux);
                    params = params.set('numPresta', 0); // L'appel concerne l'intégralité de la facture

                    params = params.set('facture', xml);
                  } catch (e: any) {
                    processingResult.addFatal('Echec de construction de la facture: ' + e);
                    return of(processingResult);
                  }
                  const numIdentFact = user?.data?.FSE?.PS?.numIdentFact;
                  if (topAssistance) {
                    // Mode assistance STS
                    return this.areaapi.postData(area_baseurl, 'assistanceSTS', params, numIdentFact).pipe(
                      map((areaResponse: dao.AreaResponseType) => {
                        processingResult.setAreaResponse(areaResponse);
                        if (areaResponse.HTTPResponse.codeRetour != '0') {
                          processingResult.addError(`${getStatusCodeByValue(+areaResponse.HTTPResponse.codeRetour)?.nom} ${areaResponse.HTTPResponse.libRetour}`);
                          console.log(`[facturer] mode assistance STS. Code retour: ${getStatusCodeByValue(+areaResponse.HTTPResponse.codeRetour)?.nom}`);
                        }
                        return processingResult;
                      }),
                    );
                  } else {
                    // Facturation OU simulation
                    return this.areaapi.postData(area_baseurl, 'facturer', params, numIdentFact).pipe(
                      map((areaResponse: dao.AreaResponseType) => {
                        processingResult.setAreaResponse(areaResponse);
                        if (areaResponse.HTTPResponse.codeRetour != '0') {
                          // TODO Check for Cancel (ABORT)
                          processingResult.addError(`${getStatusCodeByValue(+areaResponse.HTTPResponse.codeRetour)?.nom} ${areaResponse.HTTPResponse.libRetour}`);
                        }
                        return processingResult;
                      }),
                    );
                  }
                }),
              );
            }
          } catch (error: any) {
            processingResult.addFatal(error);
            return of(processingResult);
          }
        } else {
          // Error occured, already logged previously
          return of(processingResult);
        }
      }),
    );
    return paramObs$;
  }

  /* Appel de l'API creerDREAnnulation */
  public creerDREAnnulation$(area_baseurl: string, numIdentFact: string, numFacture: string, dateFacture: string, modeSecur: string, nomRessLect: string): Observable<dao.AreaResponseType | undefined> {
    // TEMPLATE Build query parameters safely and encode values
    const params = new URLSearchParams();
    params.set('nomRessLect', nomRessLect);
    params.set('codePorteurPS', ''); // keep empty if intended
    params.set('numIdentFact', numIdentFact);
    params.set('numFacture', numFacture);
    params.set('dateFacture', dateFacture);
    params.set('modeSecur', modeSecur);

    let url = `${area_baseurl}creerDREAnnulation?${params.toString()}`;
    return this.areaapi.getData(area_baseurl, url, numIdentFact).pipe(
      switchMap(xmldata => {
        return new Observable<dao.AreaResponseType>(observer => {
          this.xmlparser.parseString(xmldata ?? '', (err: any, response: dao.AreaResponseType) => {
            if (err) {
              console.error('[getData] JSON parsing error: ', err);
              observer.next(err); // TODO Deal w/error
            } else {
              //console.log("jsono: ", JSON.stringify(jsono));
              observer.next(response);
              observer.complete();
            }
          });
        });
      }),
      catchError(error => {
        console.error('[lireCV] Error fetching data: ', error);
        return of(undefined); // Use of(undefined) to propagate errors
      }),
    );
  }

  public getPrestationFRs(server_url: string, id_order: number, factureFR: dao.FactureFR, processingResult: utils.ProcessingResult): Observable<dao.PrestationFR[]> {
    let error: boolean = false;
    if (factureFR.prestationFR != undefined && (factureFR.prestationFR as dao.PrestationFR[]).length !== 0) {
      // prestationFR exists from a previous simulation, return it
      console.log('[getPrestationFRs] factureFR.prestationFR exists, returning it');
      const fseRefDate = utils.tofseformat(factureFR.dateRefAMC);
      if (fseRefDate) {
        for (const p of factureFR.prestationFR as dao.PrestationFR[]) {
          if (p.LPP?.typePrestaLPP !== 'L') {
            p.dateExec = fseRefDate;
            if (p.LPP) {
              p.LPP.dateDebut = fseRefDate;
              if (p.LPP.dateFin) {
                p.LPP.dateFin = fseRefDate;
              }
            }
          }
        }
      }
      return of(factureFR.prestationFR).pipe(take(1));
    }
    //const order: any[] = this.apiclientService.getOrders$(server_url, id_order);									// Get order detail
    let ret: dao.PrestationFR[] = new Array<dao.PrestationFR>();
    return this.apiclientService.getOrders$(server_url, id_order).pipe(
      tap({
        next: (_order: any[]) => {},
      }),
      switchMap((order: any[]) => {
        //console.log(order[0].hasOwnProperty("lines") + ' - ' + order[0].lines.length);
        if (!order || !order[0] || !order[0].hasOwnProperty('lines') || !order[0].lines || !order[0].lines.length) {
          // No lines in order
          console.error('No lines in order');
          processingResult.addError('La commande ne contient pas de ligne');
          error = true;
          return of([]);
        }
        let lines = order[0].lines;
        if (!lines || !lines.length) {
          console.error('No lines in order');
          processingResult.addError('La commande ne contient pas de ligne éligible');
          error = true;
          return of([]);
        }
        const lineObservables = lines.map((line: any) => {
          // Create an array of observables for each line
          console.log('[getPrestationFRs] Processing line: ', JSON.stringify(line, null, 2));
          const isRental = line.typ === 'RENTAL' || line.typ === 6 || line.rental_terms_snapshot != null || line.rental_terms != null;
          // Pour établir le modèle de FSE location, toutes les dates sont à la date du jour (placeholders de simulation STS)
          // Pour une vente classique, on conserve dateRefAMC
          const todayFSE = utils.dateTofseFormat(new Date()); // YYYYMMDD
          const todayFR = utils.toFRformat(todayFSE); // DD/MM/YYYY
          const datreflpp = isRental ? todayFR : factureFR.dateRefAMC;

          return this.apiclientService.getProduct(server_url, line.product, datreflpp).pipe(
            tap({
              next: _product => {
                /* console.log("After getProductstpl$, res: ", JSON.stringify(res, null, 2)); */
              },
            }),
            switchMap((res: dao.ApiResponse<any>) => {
              if (!res || typeof res !== 'object' || typeof res.success !== 'boolean') {
                const msg = `Erreur serveur au chargement de l'article ${line.product}`;
                processingResult.addFatal(msg);
                error = true;
                return of(null);
              }
              if (!res.success) {
                const serverMsg = res.error_msg || res.msg || `Erreur serveur au chargement de l'article ${line.product}`;
                processingResult.addFatal(serverMsg);
                error = true;
                return of(null);
              }
              return of(res.data);
            }),
            map(product => {
              if (!product) return [];
              try {
                // Date manip. could go wrong
                console.log('[getPrestationFRs] Processing product: ', JSON.stringify(product, null, 2));

                if (isRental) {
                  // Contrat de location : sélectionner le Palier 1 (Solution A1)
                  const chosenRtId = line.rental_terms_snapshot?.id ?? line.rental_terms?.lnk_rental_terms?.id;
                  const rt = product?.lnk_rental_terms?.find((r: any) => r.id === chosenRtId) || product?.lnk_rental_terms?.[0];
                  const tier1 = rt?.tiered_pricing?.find((tp: any) => tp.starting_period === 1) || rt?.tiered_pricing?.[0];
                  const lnk_lpp = tier1?.lnk_lpp;

                  if (!lnk_lpp || !lnk_lpp.id) {
                    console.log(`[getPrestationFRs] Pas de code LPP actif pour la location sur le produit: ${product?.id}`);
                    processingResult.addWarning(`Pas de code LPP actif pour la location sur le produit: ${product?.id}`);
                  } else {
                    const vatRate = parseFloat(rt?.vat_rate ?? product?.lnk_template?.vat_rate) || 0;
                    const tier1UpriceTaxi = tier1?.uprice_taxx ? +(tier1.uprice_taxx * (1 + vatRate / 100)).toFixed(4) : 0;
                    const tier1PriceCents = Math.round(tier1UpriceTaxi * 100);
                    const plvCents = Math.round(+lnk_lpp.plv * 100);
                    const depassementPlv: boolean = plvCents > 0 && plvCents < tier1PriceCents;
                    if (depassementPlv) {
                      processingResult.addInfo("Le montant de la prestation facturée est supérieur au Prix Limite de Vente. Il vous appartient d'en informer votre patient");
                    }
                    const lpp = new dao.LPP({
                      codeLPP: lnk_lpp.id === 'PA' ? '' : lnk_lpp.id,
                      typePrestaLPP: 'L', // Location
                      // Dates are just placeholders for now (get template from Area). Will be replaced by the rental terms actual dates during invoicing
                      dateDebut: todayFSE,
                      dateFin: todayFSE,
                      tarifLPP: String(Math.round(+lnk_lpp.tarifref * 100)),
                      nbUnites: line.qty ?? 1,
                      prixUnitairePublicLPP: String(tier1PriceCents),
                      mtTotalLPP: String(+(String(Math.round(+lnk_lpp.tarifref * 100)) ?? 0) * +(line.qty ?? 1)),
                      mtTotalTTC: undefined,
                      prixUnitaireReglemente: depassementPlv ? String(plvCents) : undefined,
                    });

                    console.log(`%c[getPrestationFRs] building Rental LPP: ${JSON.stringify(lpp, null, 2)}`, 'color: brown;');

                    const p: dao.PrestationFR = new dao.PrestationFR({
                      originePresta: 'PS',
                      topDomicile: '0',
                      dateExec: utils.tofseformat(factureFR.dateRefAMC) || todayFSE,
                      codePresta: lnk_lpp.prestation,
                      montantHonoraires: undefined,
                      qualifDepense: lnk_lpp.is_valid ? '' : 'N',
                      prixUnit: String(+(String(Math.round(+lnk_lpp.tarifref * 100)) ?? 0) * +(line.qty ?? 1)),
                      codeJustifETM: '0',
                      accordEP: '',
                      dateDemandeEP: '',
                      topPreventionCommuneAMOAMC: '0',
                      plafondCMU: '',
                      LPP: lpp,
                    });

                    // Cas particulier AME (mutnum=75500017)
                    if ((factureFR.beneficiaire?.attestationAMO?.pieceJustifAMO == dao.PieceJustifAMO.ATTESTATION || factureFR.beneficiaire?.attestationAMO?.pieceJustifAMO == dao.PieceJustifAMO.ADR) && factureFR.beneficiaire?.attestationAMC?.mutnum == '75500017') {
                      p.qualifDepense = 'N';
                    }
                    ret.push(p);
                  }
                } else {
                  // Process the template and line to create a PrestationFR (Comportement Standard / Vente)
                  let lnk_cuf = product?.lnk_cufs?.cufs?.['5'];
                  let lpp = undefined;
                  if (lnk_cuf == undefined || lnk_cuf.id == '') {
                    // LPP not found in product.lnk_cufs.5
                    console.log(`[getPrestationFRs] Pas de code LPP actif sur le produit: ${product?.id}`);
                    processingResult.addWarning(`Pas de code LPP actif sur le produit: ${product?.id}`); // Warn user
                  } else {
                    const billedPriceTaxi = line.unet_taxi !== undefined && line.unet_taxi !== null ? +line.unet_taxi : +line.uprice_taxi;
                    const billedPriceCents = Math.round(billedPriceTaxi * 100);
                    const plvCents = Math.round(+lnk_cuf.plv * 100);
                    let depassementPlv: boolean = plvCents > 0 && plvCents < billedPriceCents;
                    if (depassementPlv) {
                      processingResult.addInfo("Le montant de la prestation facturée est supérieur au Prix Limite de Vente. Il vous appartient d'en informer votre patient");
                      //alert("Le montant de la prestation facturée est supérieur au Prix Limite de Vente. Il vous appartient d'en informer votre patient");
                    }
                    lpp = new dao.LPP({
                      codeLPP: lnk_cuf.id === 'PA' ? '' : lnk_cuf.id, // PA is a fake id meaning NO LPP
                      typePrestaLPP: lnk_cuf.type_prestation,
                      dateDebut: utils.tofseformat(factureFR.dateRefAMC), // WARNING date prestation = date de soin Attention aux locations
                      dateFin: utils.tofseformat(factureFR.dateRefAMC), // WARNING date prestation = date de soin Attention aux locations
                      tarifLPP: String(Math.round(+lnk_cuf.tarifref * 100)) /** */,
                      nbUnites: line.qty /** */,
                      prixUnitairePublicLPP: String(billedPriceCents) /** */,
                      mtTotalLPP: String(+(String(Math.round(+lnk_cuf.tarifref * 100)) ?? 0) * +(line.qty ?? 0)) /** */,
                      mtTotalTTC: undefined, // Calculated in toOrderedObject
                      prixUnitaireReglemente: depassementPlv ? String(plvCents) : undefined,
                    });
                  }

                  console.log(`%c[getPrestationFRs] building LPP:  ${JSON.stringify(product, null, 2)}`, 'color: brown;');

                  let p: dao.PrestationFR = new dao.PrestationFR({
                    originePresta: 'PS',
                    topDomicile: '0',
                    dateExec: utils.tofseformat(factureFR.dateRefAMC) || todayFSE,
                    codePresta: lnk_cuf.prestation,
                    montantHonoraires: undefined, // Calculated in toOrderedObject
                    qualifDepense: lnk_cuf.is_valid ? '' : 'N', // La LPP n'a pas de période de validité active => qualifDepense = N
                    prixUnit: String(+(String(Math.round(+lnk_cuf.tarifref * 100)) ?? 0) * +(line.qty ?? 0)) /** */,
                    codeJustifETM: '0', // Defaults to "Pas d'exonération"
                    accordEP: '', // Defaults to empty (can be filled in Presta detail)
                    dateDemandeEP: '',
                    topPreventionCommuneAMOAMC: '0', // Not managed for now
                    plafondCMU: '', // Input by PS
                    LPP: lpp,
                  });
                  // Cas particulier AME (mutnum=75500017). Set qualifDepense to 'G', amounts = 0 (en cas d'attestation (1) OU ADRi (A))
                  if ((factureFR.beneficiaire.attestationAMO.pieceJustifAMO == dao.PieceJustifAMO.ATTESTATION || factureFR.beneficiaire.attestationAMO.pieceJustifAMO == dao.PieceJustifAMO.ADR) && factureFR.beneficiaire.attestationAMC.mutnum == '75500017') {
                    p.qualifDepense = 'N';
                  }
                  ret.push(p);
                }
                return ret;
              } catch (e: any) {
                processingResult.addFatal('Erreur au traitement de la prestation : ' + e);
                error = true;
                return of([]);
              }
            }),
            catchError(err => {
              console.error('Error in getProductstpl$: ', err);
              if (!error) {
                processingResult.addError(err?.message || err);
                error = true;
              }
              return of([]);
            }),
          );
        });

        // Use forkJoin to wait for all line observables to complete
        return forkJoin(lineObservables).pipe(
          map(() => {
            if (error) {
              if (processingResult.fatal.length === 0) {
                processingResult.addFatal('Chargement des prestations - Error occurred');
              }
              return [];
            }
            return ret;
          }),
          catchError(err => {
            console.error('Error in forkJoin: ', err);
            processingResult.addFatal('Chargement des prestations - Error occurred');
            return of([]);
          }),
        );
      }),
    );
  }
}
// Low level AreFSE API

@Injectable({
  providedIn: 'root',
})
export class AreaApiService {
  constructor(private http: HttpClient) {
    this.maxretry = 5;
  }

  idSession: string | undefined;
  maxretry: number = 1;
  xmlparser: xml2js.Parser = new xml2js.Parser({
    strict: true,
    trim: false,
    explicitArray: false,
  });

  public getData(area_baseurl: string, target_url: string, numIdentFact: string): Observable<string | undefined> {
    if (!numIdentFact) {
      console.error('[getData] numIdentFact parameter is mandatory');
      return throwError(() => new Error('[getData] numIdentFact parameter is mandatory'));
    }
    const maxRetry = 3; // Just to avoid looping forever
    let retryCount = 0; // Initialize retry count

    const get$ = (): Observable<string | undefined> => {
      return this.getSessionId(area_baseurl, numIdentFact).pipe(
        switchMap(idSession => {
          if (!idSession) {
            throw new Error(`ERROR getData(${area_baseurl}) can't get a session Id`);
          }

          this.idSession = idSession;
          const url = `${target_url}&idSession=${this.idSession}`;

          return this.http.get(url, { responseType: 'text' }).pipe(
            //tap(res=>console.log(`[getData] res: ${res}`)),
            switchMap(xmldata => {
              return new Observable<string>(observer => {
                this.xmlparser.parseString(xmldata, (err: any, jsono: dao.AreaResponseType) => {
                  if (err) {
                    observer.next(xmldata);
                    observer.complete();
                  } else {
                    if (jsono.HTTPResponse && jsono.HTTPResponse.codeRetour === '-5') {
                      console.error('[getData] Session expired or invalid. Clearing session and retrying.');
                      this.idSession = undefined;
                      observer.error('Invalid Session');
                    } else {
                      observer.next(xmldata);
                      observer.complete();
                    }
                  }
                });
              });
            }),
            catchError(error => {
              console.error('[getData] Error detected: ', error);
              if (retryCount < maxRetry) {
                retryCount++;
                console.log(`[getData] Retrying... attempt ${retryCount}`);
                return of(null).pipe(
                  delay(500),
                  switchMap(() => get$()),
                );
              } else {
                console.error('[getData] Max retries reached. Failing.');
                return throwError(() => new Error('Max retries reached for getData'));
              }
            }),
          );
        }),
      );
    };

    return get$();
  }

  public postData(area_baseurl: string, target_url: string, httpParams: HttpParams, numIdentFact: string): Observable<any> {
    const actualNumIdentFact = numIdentFact || httpParams.get('numIdentFact') || undefined;
    if (!actualNumIdentFact) {
      console.error(`[postData] numIdentFact parameter is mandatory for target_url: ${target_url}`);
      return throwError(() => new Error(`[postData] numIdentFact parameter is mandatory for target_url: ${target_url}`));
    }

    const maxRetry = 1;
    let retryCount = 0;

    const post$ = (): Observable<any> => {
      return this.getSessionId(area_baseurl, actualNumIdentFact).pipe(
        switchMap(idSession => {
          if (!idSession) {
            throw new Error(`ERROR postData(${area_baseurl}) can't get a session Id`);
          }

          let updatedParams = httpParams.set('idSession', idSession);
          if (!updatedParams.has('numIdentFact')) {
            updatedParams = updatedParams.set('numIdentFact', actualNumIdentFact);
          }

          return this.http
            .post(area_baseurl + target_url, updatedParams, {
              responseType: 'text',
            })
            .pipe(
              switchMap(xmldata => {
                return new Observable<any>(observer => {
                  this.xmlparser.parseString(xmldata, (err: any, jsono: any) => {
                    if (err) {
                      console.error('[postData] JSON parsing error: ', err);
                      observer.error(err);
                      return;
                    }

                    const codeRetour = jsono?.HTTPResponse?.codeRetour;
                    const libRetour = jsono?.HTTPResponse?.libRetour || '';
                    const isSessionExpired = String(codeRetour) === '-5' || libRetour.includes('Session introuvable');

                    if (isSessionExpired) {
                      console.warn(`[postData] Session expired or invalid (codeRetour: ${codeRetour}, lib: ${libRetour}). Clearing session.`);
                      this.idSession = undefined;
                      if (retryCount < maxRetry) {
                        retryCount++;
                        console.log(`[postData] Retrying with new session... (attempt ${retryCount}/${maxRetry})`);
                        post$().subscribe({
                          next: res => {
                            observer.next(res);
                            observer.complete();
                          },
                          error: e => observer.error(e),
                        });
                        return;
                      }
                    }

                    observer.next(jsono);
                    observer.complete();
                  });
                });
              }),
              catchError(err => {
                console.error(`[postData] ERROR executing request (${target_url}): `, err);
                return throwError(() => err);
              }),
            );
        }),
      );
    };

    return post$();
  }

  public getSessionId(area_baseurl: string, numIdentFact: string): Observable<string | undefined> {
    if (!numIdentFact) {
      console.error('[getSessionId] numIdentFact parameter is mandatory');
      return throwError(() => new Error('[getSessionId] numIdentFact parameter is mandatory'));
    }
    if (this.idSession) {
      return of(this.idSession);
    }
    return this.ouvrirSession(area_baseurl, numIdentFact).pipe(
      switchMap(xmldata => {
        return new Observable<string | undefined>(observer => {
          this.xmlparser.parseString(xmldata, (err: any, jsono: dao.AreaResponseType) => {
            if (err) {
              console.log('Json parsing error: ', err);
              observer.error(err);
            } else {
              //console.dir(JSON.stringify(jsono));
              this.idSession = jsono.HTTPResponse.idSession;
              observer.next(this.idSession);
              observer.complete();
            }
          });
        });
      }),
      catchError(error => {
        console.log("ERROR Can't connect to AreaFSE server", error);
        return of(undefined);
      }),
    );
  }

  public ouvrirSession(baseurl: string, numIdentFact: string): Observable<any> {
    console.log('In ouvrirSession');
    if (!numIdentFact) {
      console.error('[ouvrirSession] numIdentFact parameter is mandatory');
      return throwError(() => new Error('[ouvrirSession] numIdentFact parameter is mandatory'));
    }
    const url = baseurl + 'ouvrirSession';
    const params = new HttpParams().set('numIdentFact', numIdentFact);

    return this.http.post(url, params, { responseType: 'text' }).pipe(
      catchError(e => {
        console.error('ERROR ouvrirSession ' + JSON.stringify(e, null, 2));
        throw new Error('ERROR ouvrirSession ' + JSON.stringify(e));
      }),
    );
  }
}
